import React, { useState, useCallback, useEffect } from 'react';
import { useDxfWidgets } from '../hooks/useDxfWidgets';
import {
  LargeArea as LibLargeArea,
  GaugeBar as LibGaugeBar,
  Gauge as LibGauge,
  AreaPage as LibAreaPage,
  GaugePage as LibGaugePage,
  ColumnChart as LibColumnChart,
  Kpi as LibKpi,
} from 'processhub-components-lib';
import tabBodyConfig from 'config/dashboard/tabBodyConfig';
import allHooks from 'hooks';
import { WidgetsProvider } from 'hooks/dynamicWidgets/useWidgets';
import { WidgetsDataProvider } from 'hooks/dynamicWidgets/useWidgetsData';
import { TabsProvider } from 'hooks/dynamicWidgets/useTabs';
import { getTagData, getAllTags } from 'api/tags';
import { getExpressionData, getAllExpressions } from 'api/expression';
import Large from 'components/UnityCharts/charts/Large/Large';
import GaugeBar from 'components/UnityCharts/charts/GaugeBar/GaugeBar';
import AreaPage from 'components/UnityCharts/charts/Area/Area';
import GaugePage from 'components/UnityCharts/charts/Gauge/Gauge';
import ColumnChart from 'components/UnityCharts/charts/Column/Column';
import Kpi from 'components/UnityCharts/charts/Kpi/Kpi';
import OneGauge from 'components/UnityCharts/charts/OneGauge/OneGauge';
import oneGaugeConfig from 'config/charts/oneGaugeConfig';
import useApp from 'hooks/useApp';

const WidgetLayer: React.FC<{ isWidgetResizing: boolean; widgetResizingId: string | null; setIsWidgetResizing: (isWidgetResizing: boolean) => void; setWidgetResizingId: (widgetResizingId: string | null) => void; movingItem: { type: 'comment' | 'label' | 'widget' | 'document'; id: string } | null; showWidgets: boolean, colorTheme?: 'sdaia' | 'default' }> = ({ isWidgetResizing, widgetResizingId, setIsWidgetResizing, setWidgetResizingId, movingItem, showWidgets, colorTheme = 'default' }) => {
  const { widgets, widgetData, getWorldToCanvas, updateWidgetScale, updateWidgetFixedSize, removeWidget, setWidgetContextMenu, currentZoom, getWidgetDataForWidget } = useDxfWidgets();
  
  const [chartType, setChartType] = useState<"gauge" | "area">("gauge");
  const { activeTabId, tabsData } = useApp();
  
  const currentTab: any = tabsData.find((t: any) => t.id === activeTabId);

  // Function to convert world coordinates to screen coordinates (same as comments/labels)
  const worldToScreen = (worldX: number, worldY: number) => {
    const worldToCanvasFn = getWorldToCanvas();
    if (worldToCanvasFn) {
      // Use the exact same coordinate transformation as comments and labels
      return worldToCanvasFn(worldX, worldY);
    }

    // Fallback to center if worldToCanvas function not available
    return {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2
    };
  };

  // Function to get the current zoom scale for widgets
  const getWidgetZoomScale = (widget: any) => {
    // Check if we have a local scale for this widget first (for immediate feedback)
    // Fall back to the widget's scale from backend data
    const baseScale = localWidgetScales[widget.id] !== undefined
      ? localWidgetScales[widget.id]
      : (widget.scale || 0.3);

    if (widget.fixedSize) {
      // Fixed size behavior: maintain consistent visual size regardless of zoom
      // Use direct scaling to maintain pixel size
      return baseScale * currentZoom;
    } else {
      // Normal behavior: maintain consistent size regardless of zoom
      // Use direct scaling to maintain pixel size
      return baseScale * currentZoom;
    }
  };

  // Create a hooks object that includes the API functions at the top level
  const hooksWithApi = {
    ...allHooks,
    // Add API functions directly to the hooks object
    getTagData,
    getAllTags,
    getExpressionData,
    getAllExpressions,
  };

  // Create a tabBodyConfig with the API functions
  const tabBodyConfigWithApi = {
    ...tabBodyConfig,
    allConfigurationApiConfig: {
      getAllTags,
      getTagData,
      getAllExpressions,
      getExpressionData
    }
  };


  // Use movingItem prop instead of local state
  const [resizingWidget, setResizingWidget] = useState<string | null>(null);

  // Local state to store widget scales for immediate visual updates
  const [localWidgetScales, setLocalWidgetScales] = useState<Record<string, number>>({});

  // Handle widget context menu
  const handleWidgetContextMenu = useCallback((e: React.MouseEvent, widgetId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setWidgetContextMenu(widgetId, e.clientX, e.clientY);
  }, [setWidgetContextMenu]);

  // Widget movement is now handled by the parent component via movingItem prop

  // Handle widget resize
  const handleWidgetResize = (widgetId: string) => {
    const widget = widgets.find((w) => w.id === widgetId);
    if (!widget) return;

    const startX = window.event instanceof MouseEvent ? window.event.clientX : 0;
    const initialScale = localWidgetScales[widgetId] ?? widget.scale ?? 1;

    const onMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - startX;
      const newScale = Math.max(0.1, initialScale + dx * 0.005); // Adjust sensitivity
      const clampedScale = newScale; // No upper limit

      setLocalWidgetScales(prev => ({
        ...prev,
        [widgetId]: clampedScale,
      }));

      updateWidgetScale(widgetId, clampedScale);
    };

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);

      setIsWidgetResizing(false);
      setWidgetResizingId(null);

      // Final update - the scale is already saved during mouse move
      const finalScale = localWidgetScales[widgetId] ?? widget.scale;
      // No need for final update since we're already calling updateWidgetScale during mouse move
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    setResizingWidget(widgetId);
    setIsWidgetResizing(true);
    setWidgetResizingId(widgetId);
  };


  // Handle mouse events for moving and resizing widgets
  const handleWidgetMouseDown = useCallback((e: React.MouseEvent, widgetId: string) => {
    if ((movingItem?.type === 'widget' && movingItem?.id === widgetId) || resizingWidget === widgetId) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, [movingItem, resizingWidget]);

  const handleWidgetMouseMove = useCallback((e: React.MouseEvent) => {
    if (resizingWidget) {
      const widget = widgets.find(w => w.id === resizingWidget);
      if (!widget) return;

      // Get the base scale, preferring local state if available
      const baseScale = localWidgetScales[widget.id] !== undefined
        ? localWidgetScales[widget.id]
        : widget.scale;

      const newScale = baseScale + e.movementX * 0.005; // Adjust 0.005 for sensitivity
      const clampedScale = Math.max(0.1, newScale); // Only minimum limit, no maximum
       // Update local state for immediate visual feedback
      setLocalWidgetScales(prev => ({
        ...prev,
        [widget.id]: clampedScale
      }));

      // During mouse move, use normal updateWidgetScale without the final flag
      updateWidgetScale(widget.id, clampedScale);
      
      // Automatically set fixedSize to true when widget is manually resized
      if (!widget.fixedSize) {
        updateWidgetFixedSize(widget.id, true);
      }
    }
  }, [resizingWidget, widgets, updateWidgetScale, updateWidgetFixedSize, localWidgetScales]);

  // Handle clicks anywhere to disable resize mode
  useEffect(() => {
    const handleGlobalClick = (e: MouseEvent) => {
      if (resizingWidget) {
        // Check if the click is on the resize handle
        const resizeHandle = document.querySelector(`[data-resize-handle="${resizingWidget}"]`);
        if (resizeHandle && resizeHandle.contains(e.target as Node)) {
          // Click was on the resize handle, don't cancel resize
          return;
        }

        // Click was elsewhere, cancel resize mode
        setResizingWidget(null);
      }
    };

    // Add global click listener
    document.addEventListener('click', handleGlobalClick);

    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, [resizingWidget]);

  const handleWidgetMouseUp = useCallback(() => {
    // Only handle movingWidget here, resizingWidget is handled by the global click
    // Movement is now handled by parent component via movingItem prop
  }, []);


  // Handle widget delete
  const handleWidgetDelete = useCallback((widgetId: string) => {
    removeWidget(widgetId);
  }, [removeWidget]);

  // Handle widget edit (for now, just close menu - could open edit modal later)
  const handleWidgetEdit = useCallback((widgetId: string) => {
    // TODO: Implement widget editing
  }, []);

  // Handle widget fixed size toggle
  const handleWidgetFixedSize = useCallback((widgetId: string) => {
    const widget = widgets.find(w => w.id === widgetId);
    if (widget) {
      updateWidgetFixedSize(widgetId, !widget.fixedSize);
    }
  }, [widgets, updateWidgetFixedSize]);

  // Function to render the appropriate chart component based on type
  const renderChartComponent = (widget: any, tab: any) => {
    const isSdaia = colorTheme === 'sdaia';
    switch (widget.type) {
      case 'large':
        return (
          <div className={`${isSdaia ? 'new-large-widget' : 'large-widget'} relative`}>
            <h1 className="text-[#137b70] text-xl font-semibold absolute -top-10 left-0 px-2 text-green">{widget.config.name}</h1>
            {isSdaia ? (
              <Large
                tab={tab}
                hooks={hooksWithApi}
                largeConfig={tabBodyConfigWithApi.largeConfig}
              />
            ) : (
              <LibLargeArea
                tab={tab}
                hooks={hooksWithApi}
                largeConfig={tabBodyConfigWithApi.largeConfig}
              />
            )}
          </div>
        );

      case 'gaugeBar': {
        // Always render the gauge bar - let it handle its own data validation
        return (
          <>
            <h1 className="text-[#137b70] text-xl font-semibold absolute -top-10 left-0 px-2 text-green">{widget.config.name}</h1>
            {isSdaia ? (
              <GaugeBar
                tab={tab}
                hooks={hooksWithApi}
                setChartTypeComp={setChartType}
                unityGaugeBarConfig={tabBodyConfigWithApi.unityGaugeBarConfig}
              />
            ) : (
              <LibGaugeBar
                tab={tab}
                hooks={hooksWithApi}
                unityGaugeBarConfig={tabBodyConfigWithApi.unityGaugeBarConfig}
                theme={colorTheme}
              />
            )}
          </>
        );
      }

      case 'gauge': {
        // Always render the gauge - let it handle its own data validation
        const gaugeData = tab.configurations.k1?.value;
        const gaugeName = tab.configurations.k1?.name || widget.config.name;
        
        return (
          <div className={`${isSdaia ? 'new-gauge-widget' : 'gauge-widget'}`}>
            <h1 className="text-[#137b70] text-xl font-semibold absolute -top-10 left-0 px-2 text-green">{widget.config.name}</h1>
            {isSdaia ? (
                <OneGauge tab={tab} oneGaugeConfig={oneGaugeConfig} hooks={allHooks} />
                
            ) : (
              <LibGauge
                data={gaugeData}
                name={gaugeName}
                gaugeType='unity'
              />
            )}
          </div>
        );
      }

      case 'area': {
        // AreaPage expects a different structure - flatten the configurations
        const areaTab = {
          ...tab,
          configurations: {
            id: tab.configurations.k1.id,
            name: tab.configurations.k1.name,
            value: tab.configurations.k1.value,
            type: tab.configurations.k1.type
          }
        };
        return (
          <>

            <h1 className="text-[#137b70] text-xl font-semibold absolute -top-10 left-0 px-2 text-green">{widget.config.name}</h1>
            {isSdaia ? (
              <AreaPage
                tab={areaTab}
                hooks={hooksWithApi}
                unityAreaConfig={tabBodyConfigWithApi.unityAreaConfig}
              />
            ) : (
              <LibAreaPage
                tab={areaTab}
                hooks={hooksWithApi}
                unityAreaConfig={tabBodyConfigWithApi.unityAreaConfig}
                theme={colorTheme}
              />
            )}
          </>
        );
      }

      case 'gaugePage':
        return (
          <>

            <h1 className="text-[#137b70] text-xl font-semibold absolute -top-10 left-0 px-2 text-green">{widget.config.name}</h1>
            {isSdaia ? (
              <GaugePage
                tab={tab}
                hooks={allHooks}
                unityGaugeConfig={tabBodyConfigWithApi.unityGaugeConfig}
              />
            ) : (
              <LibGaugePage
                tab={tab}
                hooks={allHooks}
                unityGaugeConfig={tabBodyConfigWithApi.unityGaugeConfig}
              />
            )}
          </>
        );

      case 'column':
        return (
          <>

            <h1 className="text-[#137b70] text-xl font-semibold absolute -top-10 left-0 px-2 text-green">{widget.config.name}</h1>
            {isSdaia ? (
              <ColumnChart
                tab={tab}
                hooks={hooksWithApi}
                unityColumnConfig={tabBodyConfigWithApi.unityColumnConfig}
              />
            ) : (
              <LibColumnChart
                tab={tab}
                hooks={hooksWithApi}
                unityColumnConfig={tabBodyConfigWithApi.unityColumnConfig}
              />
            )}
          </>
        );

      case 'kpi':
        return (
          <div className="kpi-widget">
            <h1 className="text-[#137b70] text-xl font-semibold absolute -top-10 left-0 px-2 text-green">{widget.config.name}</h1>
            {isSdaia ? (
              <Kpi
                tab={tab}
                hooks={hooksWithApi}
                kpiConfig={tabBodyConfigWithApi.kpiConfig}
              />
            ) : (
              <LibKpi
                tab={tab}
                hooks={hooksWithApi}
                kpiConfig={tabBodyConfigWithApi.kpiConfig}
              />
            )}
          </div>
        );

      default:
        return (
          <div style={{
            padding: '20px',
            background: '#1a1a1a',
            color: 'white',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            Unknown widget type: {widget.type}
          </div>
        );
    }
  };

  // Debug widget data - removed to prevent performance issues

  // Initialize local widget scales when widgets change
  useEffect(() => {
    // Create an initial map of widget scales from loaded widgets
    const initialScales: Record<string, number> = {};

    widgets.forEach(widget => {
      // Only update if we don't already have a local scale for this widget
      if (localWidgetScales[widget.id] === undefined) {
        initialScales[widget.id] = widget.scale || 0.3;
      }
    });

    // Update local scales with any new widgets
    if (Object.keys(initialScales).length > 0) {
      setLocalWidgetScales(prev => ({
        ...prev,
        ...initialScales
      }));
    }
  }, [widgets]);

  // State for tracking which widgets to force show
  const [forceShowWidgets, setForceShowWidgets] = useState<Record<string, boolean>>({});

  // Effect to handle force showing widgets after timeout - optimized to prevent loops
  useEffect(() => {
    const timers: Record<string, NodeJS.Timeout> = {};

    // For each widget that doesn't have data, set a timer
    widgets.forEach(w => {
      const hasData = w.config.tagIds.some(id => widgetData[w.id]?.[id]);
      if (!hasData && !forceShowWidgets[w.id]) {
        timers[w.id] = setTimeout(() => {
          setForceShowWidgets(prev => ({
            ...prev,
            [w.id]: true
          }));
        }, 5000);
      }
    });
    
    // Cleanup timers
    return () => {
      Object.values(timers).forEach(timer => clearTimeout(timer));
    };
  }, [widgets, widgetData]); // Removed forceShowWidgets from dependencies to prevent loops

  return (
    <>
      {/* Render the widgets */}
      {showWidgets && widgets.map((w) => {
          // Skip widgets without tag IDs - render them normally
          if (!w.config.tagIds || w.config.tagIds.length === 0) {
            return null;
          }

          // Create the tab configuration that the chart components expect
          const tab = {
            name: w.config.name,
            type: w.type,
            configured: true,
            id: w.id,
            configurations: w.config.tagIds.reduce((acc: any, id, idx) => {
              // Ensure we always have a valid ID
              if (!id || isNaN(Number(id))) {
                return acc;
              }
              // Get the actual tag data using the appropriate data source (persistent for gauges)
              const widgetDataForWidget = getWidgetDataForWidget(w.id, w.type);
              const tagData = widgetDataForWidget[id];

              // Always create a configuration, even with invalid data
              // This prevents chart components from receiving undefined data

              // Create the configuration object that chart components expect
              // Always provide valid data structure, even when tagData is undefined
              acc[`k${idx + 1}`] = {
                id: Number(id), // Convert to number like the working example
                name: w.config.tagNames?.[idx] || tagData?.name || `Tag ${id}`,
                type: "tags", // Use "tags" (plural) like the working example
                value: {
                  id: Number(id), // Convert to number
                  name: w.config.tagNames?.[idx] || tagData?.name || `Tag ${id}`,
                  desc: tagData?.desc || null,
                  formattedValue: tagData?.formattedValue || "N/A", // Use "N/A" as fallback like working example
                  uom: tagData?.uom || '',
                  haveValue: tagData?.haveValue !== false,
                  value: tagData?.value || null, // Use null as fallback like working example
                  minRange: tagData?.minRange || 0,
                  maxRange: tagData?.maxRange || 100,
                  minValue: tagData?.minValue || 0,
                  maxValue: tagData?.maxValue || 100,
                  plantId: tagData?.plantId || null,
                  plantName: tagData?.plantName || null,
                  trainId: tagData?.trainId || null,
                  trainName: tagData?.trainName || null,
                  unitId: tagData?.unitId || null,
                  equipmentId: tagData?.equipmentId ? Number(tagData.equipmentId) : null, // Convert to number
                  equipmentName: tagData?.equipmentName || null,
                  equipmentBubbleId: tagData?.equipmentBubbleId || null,
                  errorMessage: tagData?.errorMessage || null,
                  tagNameIdentifier: tagData?.tagNameIdentifier || `tag_${id}`,
                  unitName: tagData?.unitName || null,
                  isDisabled: tagData?.isDisabled || false,
                  isPinnedReact: tagData?.isPinnedReact || false,
                  isPinned3D: tagData?.isPinned3D || false,
                  isTicketCreated: tagData?.isTicketCreated || false,
                  isAlertTag: tagData?.isAlertTag || false,
                  // Add default color properties to prevent Chart.js errors
                  color: tagData?.color || '#3B82F6',
                  backgroundColor: tagData?.backgroundColor || '#3B82F6',
                  borderColor: tagData?.borderColor || '#1E40AF',
                  hoverBackgroundColor: tagData?.hoverBackgroundColor || '#2563EB',
                  hoverBorderColor: tagData?.hoverBorderColor || '#1D4ED8'
                }
              };
              return acc;
            }, {}),
          };

          // Ensure configurations is never empty - add a default configuration if needed
          if (Object.keys(tab.configurations).length === 0) {
            tab.configurations.k1 = {
              id: 0,
              name: 'No Data',
              type: 'tags',
              value: {
                id: 0,
                name: 'No Data',
                desc: null,
                formattedValue: 'N/A',
                uom: '',
                haveValue: false,
                value: null,
                minRange: 0,
                maxRange: 100,
                minValue: 0,
                maxValue: 100,
                plantId: null,
                plantName: null,
                trainId: null,
                trainName: null,
                unitId: null,
                equipmentId: null,
                equipmentName: null,
                equipmentBubbleId: null,
                errorMessage: 'No data available',
                tagNameIdentifier: 'no_data',
                unitName: null,
                isDisabled: false,
                isPinnedReact: false,
                isPinned3D: false,
                isTicketCreated: false,
                isAlertTag: false,
                color: '#6B7280',
                backgroundColor: '#6B7280',
                borderColor: '#4B5563',
                hoverBackgroundColor: '#6B7280',
                hoverBorderColor: '#4B5563'
              }
            };
          }


          // Use temporary position if available (during movement), otherwise use actual position
          const useX = (w as any).tempX !== undefined ? (w as any).tempX : w.x;
          const useY = (w as any).tempY !== undefined ? (w as any).tempY : w.y;
          
          // Convert world coordinates to screen coordinates using the exact same method as comments/labels
          const screenPos = worldToScreen(useX, useY);

          const isMoving = movingItem?.type === 'widget' && movingItem?.id === w.id;
          const isResizing = resizingWidget === w.id;

          const isSdaia = colorTheme === 'sdaia';
          // No loading states - widgets render immediately

          return (
            <div
              key={w.id}
              className={`absolute dxf-widgets-container 
              ${w.type === 'gaugeBar' && 'gauge-bar-widget'} 
              ${w.type === 'gauge' && 'dxf-gauge-chart'} 
              ${w.type === 'area' && 'dxf-area-chart'} 
              ${w.type === 'column' && 'dxf-column-chart'}
            `}
              style={{
                // Use the exact same positioning as canvas elements (comments/labels)
                left: `${screenPos.x}px`,
                top: `${screenPos.y}px`,
                transform: `translate(-50%, -50%) scale(${getWidgetZoomScale(w)})`,
                transformOrigin: 'center',
                // Remove fixed width/height - let content determine size
                minWidth: 'auto',
                minHeight: 'auto',
                maxWidth: 'auto',
                maxHeight: 'auto',
                // Black rounded corners background
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                borderRadius: '12px',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                // Ensure widget stays above canvas but below UI elements (modals, context menus, etc.)
                zIndex: isMoving || isResizing ? 50 : 40,
                cursor: isMoving ? 'move' : isResizing ? 'ne-resize' : 'default',
                pointerEvents: isMoving ? 'none' : 'auto', // Disable pointer events during movement for smooth dragging
                outline: isResizing ? '2px dashed rgba(255, 255, 255, 0.5)' : 'none',
                padding: '5px',
                paddingRight: w.type === 'large' ? '0px' : '5px',
                ...(w.type === 'gaugeBar' ? {
                  width: '360px',
                  height: isSdaia ? chartType === "gauge" ? '380px' : 'auto' : '300px',
                  backfaceVisibility: 'hidden' as any,
                  WebkitBackfaceVisibility: 'hidden' as any,
                  transformStyle: 'flat' as any,
                  willChange: 'transform',
                } : {}),



                ...(w.type === 'area' ? {
                  maxWidth: '500px',
                  maxHeight: '350px',
                } : {}),

                ...(w.type === 'gauge' ? {
                  maxWidth: '500px',
                  maxHeight: '350px',
                } : {}),


              }}
              onWheel={(e) => {
                // Allow wheel events to pass through to the canvas
                e.stopPropagation();
                // Create a new wheel event and dispatch it to the canvas
                const canvasElement = e.currentTarget.parentElement?.querySelector('canvas');
                if (canvasElement) {
                  const newWheelEvent = new WheelEvent('wheel', {
                    deltaX: e.deltaX,
                    deltaY: e.deltaY,
                    deltaZ: e.deltaZ,
                    deltaMode: e.deltaMode,
                    bubbles: true,
                    cancelable: true,
                  });
                  canvasElement.dispatchEvent(newWheelEvent);
                }
              }}
              onMouseDown={isMoving ? undefined : (e) => handleWidgetMouseDown(e, w.id)}
              // onMouseMove={handleWidgetMouseMove}
              onMouseUp={isMoving ? undefined : handleWidgetMouseUp}
            >
              <div style={{
                minWidth:
                  w.type === 'large' ? '450px' :
                    w.type === 'column' ? '380px' :
                      w.type === 'gaugeBar' ? 'auto' :
                        w.type === 'gauge' ? '300px' :
                          w.type === 'area' ? '350px' :
                            w.type === 'kpi' ? '350px' :
                              '100%',
                // minHeight: w.type === 'area' ? '250px' : undefined,
                height:
                  w.type === 'large' ? 'auto' :
                    w.type === 'column' ? '300px' :
                      w.type === 'gaugeBar' ? '100%' :
                        w.type === 'gauge' ? 'auto' :
                          w.type === 'area' ? 'auto' :
                            w.type === 'kpi' ? 'auto' :
                              '100%',
                width: w.type === 'gaugeBar' ? '100%' : undefined,
                pointerEvents: isMoving ? 'none' : 'auto', // Disable pointer events during movement
                paddingRight: w.type === 'large' ? '0px' : '',
                maxWidth: 'auto',
                overflow: w.type === 'gaugeBar' ? 'hidden' : undefined,
              }}
                onWheel={(e) => {
                  // Allow wheel events to pass through to the canvas
                  e.stopPropagation();
                  // Create a new wheel event and dispatch it to the canvas
                  const canvasElement = e.currentTarget.parentElement?.parentElement?.querySelector('canvas');
                  if (canvasElement) {
                    const newWheelEvent = new WheelEvent('wheel', {
                      deltaX: e.deltaX,
                      deltaY: e.deltaY,
                      deltaZ: e.deltaZ,
                      deltaMode: e.deltaMode,
                      bubbles: true,
                      cancelable: true,
                    });
                    canvasElement.dispatchEvent(newWheelEvent);
                  }
                }}>
                {isWidgetResizing && widgetResizingId === w.id ? (
                  // Keep widget intact during resize - show last known good state
                  <WidgetsProvider>
                    <WidgetsDataProvider>
                      <TabsProvider>
                        {(() => {
                          try {
                            return renderChartComponent(w, tab);
                          } catch (error) {
                            // During resize, show a minimal placeholder instead of loading message
                            return (
                              <div className="flex items-center justify-center h-full text-gray-400">
                                <div className="text-center">
                                  <div className="text-sm text-green">{w.config.name}</div>
                                </div>
                              </div>
                            );
                          }
                        })()}
                      </TabsProvider>
                    </WidgetsDataProvider>
                  </WidgetsProvider>
                ) : (
                  // Normal widget rendering when data is available or force show is true
                <WidgetsProvider>
                  <WidgetsDataProvider>
                    <TabsProvider>
                      {(() => {
                        try {
                          return renderChartComponent(w, tab);
                        } catch (error) {
                          // Show minimal placeholder instead of loading message
                          return (
                            <div className="flex items-center justify-center h-full text-gray-400">
                              <div className="text-center">
                                <div className="text-sm text-green">{w.config.name}</div>
                              </div>
                            </div>
                          );
                        }
                      })()}
                    </TabsProvider>
                  </WidgetsDataProvider>
                </WidgetsProvider>
                )}
                {isWidgetResizing && widgetResizingId === w.id && (
                  <div
                    data-resize-handle={w.id}
                    style={{
                      position: 'absolute',
                      top: '4px',
                      right: '-4px',
                      width: '16px',
                      height: '16px',
                      cursor: 'ne-resize',
                      zIndex: 52, // Slightly higher than moving widget
                      pointerEvents: isMoving ? 'none' : 'auto', // Disable during movement
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: isResizing ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '2px',
                      border: `1px solid ${isResizing ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.3)'}`,
                      transition: 'all 0.2s ease',
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleWidgetResize(w.id); // Mark this widget as being resized
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                    }}
                    title="Resize widget"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2 10L10 2M10 10L10 2M10 2L2 2"
                        stroke="rgba(255, 255, 255, 0.7)"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}
              </div>

              {/* Three-dot menu */}
              <div
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 51, // Slightly higher than widget but lower than resize handle
                  pointerEvents: isMoving ? 'none' : 'auto', // Disable during movement
                }}
                onClick={(e) => handleWidgetContextMenu(e, w.id)}
                title="Widget options"
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <div style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.7)' }}></div>
                  <div style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.7)' }}></div>
                  <div style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.7)' }}></div>
                </div>
              </div>
            </div>
          );
        })}

      {/* Green dot indicators for moving widgets (like comments) */}
      {showWidgets && widgets.map((w) => {
        const isMoving = movingItem?.type === 'widget' && movingItem?.id === w.id;
        if (!isMoving) return null;

        const useX = (w as any).tempX !== undefined ? (w as any).tempX : w.x;
        const useY = (w as any).tempY !== undefined ? (w as any).tempY : w.y;
        const screenPos = worldToScreen(useX, useY);

        return (
          <div
            key={`green-dot-${w.id}`}
            style={{
              position: 'absolute',
              left: screenPos.x - 8,
              top: screenPos.y - 8,
              width: '16px',
              height: '16px',
              backgroundColor: '#22C55E',
              borderRadius: '50%',
              border: '2px solid #16A34A',
              zIndex: 53, // Above widget but below UI elements
              pointerEvents: 'none',
              boxShadow: '0 0 8px rgba(34, 197, 94, 0.6)',
            }}
          />
        );
      })}

    </>
  );
};

export default WidgetLayer;
